import { useState, useCallback, useEffect, useRef } from "react";
import { elakayAutoText } from "../../dataElakay";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["useGraphicOrderSave"]?.[key] || fallback;

const STORAGE_KEY = "elakay_graphic_draft";
const STORAGE_TTL = 24 * 60 * 60 * 1000; // 24h
// Two complementary auto-save mechanisms, standardized across every editor
// in the app (PageBuilder/HowWeDoItEditor/EmailPageBuilder/Graffiti
// Configurator): a debounce that fires AUTOSAVE_DELAY after the last actual
// edit (10-30s range), AND a blind periodic safety-net save
// (AUTOSAVE_INTERVAL_MS, 1-10min range) that fires regardless of activity --
// a debounce alone never fires while the customer keeps editing continuously.
const AUTOSAVE_DELAY = 30 * 1000;         // 30 seconds after the last edit
const AUTOSAVE_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const MAX_ASK_REQUESTS = 3;

const isBrowserFile = (value) => {
  return typeof File !== "undefined" && value instanceof File;
};

const hasNonEmptyString = (s) => {
  return typeof s === "string" && s.trim().length > 0;
};

const useGraphicOrderSave = ({
  productID,
  authToken = null,
  pubToken = "",
  apiUrl = null, // gardé pour compatibilité ancienne version
  orderId = null,
  mode = "customer", // "customer" | "seller"
  onCheckoutSuccess = () => {},
  onCheckoutError = () => {},
  enableAutoSave = true,
  infoSource = null
} = {}) => {
  __setElakayInfoSource(infoSource);
  const [busy, setBusy] = useState(false);
  const [busyMode, setBusyMode] = useState(null);
  const [savedStatus, setSavedStatus] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const lastHashRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Always the most recent payload handed to autoSaveDraft() -- read by the
  // periodic safety-net interval below so it can re-check/save even when
  // the debounce timer above never gets a quiet 30s window to fire.
  const lastPayloadRef = useRef(null);

  const validate = useCallback((data) => {
    const errors = [];

    if (!data) {
      errors.push("No data to validate");
      return { valid: false, errors };
    }

    if (!data.sourceType) {
      errors.push("sourceType is required");
      return { valid: false, errors };
    }

    const validTypes = ["custom", "ask", "template"];

    if (!validTypes.includes(data.sourceType)) {
      errors.push(`Invalid sourceType: "${data.sourceType}"`);
      return { valid: false, errors };
    }

    if (data.sourceType === "custom") {
      const slots = Array.isArray(data.slots) ? data.slots : [];

      if (slots.length === 0) {
        errors.push("At least one face must be configured");
        return { valid: false, errors };
      }

      const hasAnyData = slots.some((slot) => {
        const hasBackground =
          slot.url &&
          typeof slot.url === "string" &&
          slot.url.trim().length > 0 &&
          !slot.url.startsWith("blob:") &&
          !slot.url.startsWith("data:");

        if (hasBackground) return true;

        const texts = slot.slotState?.texts || [];
        const hasText = texts.some(
          (t) => t && hasNonEmptyString(t.content)
        );

        if (hasText) return true;

        const layers = slot.slotState?.layers || [];

        const hasLayer = layers.some((layer) => {
          if (!layer) return false;

          if (isBrowserFile(layer.file) || isBrowserFile(layer._fileBlob)) {
            return true;
          }

          const url = layer.urlImg || layer.src || layer.url || "";

          return (
            url &&
            typeof url === "string" &&
            !url.startsWith("blob:") &&
            !url.startsWith("data:")
          );
        });

        return hasLayer;
      });

      if (!hasAnyData) {
        errors.push("Add at least one element: a text, an image, or a background");
        return { valid: false, errors };
      }

      return { valid: true, errors: [] };
    }

    if (data.sourceType === "ask") {
      /* Seller submits DesignStudio slots — validate slots, not askSellerData */
      if (mode === "seller") {
        const slots = Array.isArray(data.slots) ? data.slots : [];

        if (slots.length === 0) {
          errors.push("Add at least one face before submitting");
          return { valid: false, errors };
        }

        const hasAnyData = slots.some((slot) => {
          const texts = slot.slotState?.texts || [];
          if (texts.some((t) => t && hasNonEmptyString(t.content))) return true;

          const layers = slot.slotState?.layers || [];
          return layers.some((layer) => {
            if (!layer) return false;
            if (isBrowserFile(layer.file) || isBrowserFile(layer._fileBlob)) return true;
            const url = layer.urlImg || layer.src || layer.url || "";
            return url && typeof url === "string" && !url.startsWith("blob:") && !url.startsWith("data:");
          });
        });

        if (!hasAnyData) {
          errors.push("Add at least one text or image to your design before submitting");
          return { valid: false, errors };
        }

        return { valid: true, errors: [] };
      }

      /* Customer submits ask request — validate askSellerData */
      const asks = Array.isArray(data.askSellerData)
        ? data.askSellerData
        : data.askSellerData
          ? [data.askSellerData]
          : [];

      const ask = asks[asks.length - 1] || {};

      const hasAnyData =
        hasNonEmptyString(ask.specialInstructions) ||
        hasNonEmptyString(ask.artistMessage) ||
        hasNonEmptyString(ask.avoidInstructions) ||
        hasNonEmptyString(ask.requiredInstructions) ||
        hasNonEmptyString(ask.detailLevel) ||
        hasNonEmptyString(ask.budget) ||
        !!ask.desiredDate ||
        (Array.isArray(ask.stylePrefs) && ask.stylePrefs.length > 0) ||
        (Array.isArray(ask.inspirations) && ask.inspirations.length > 0) ||
        (Array.isArray(ask.images) &&
          ask.images.some((r) => {
            if (!r) return false;
            if (isBrowserFile(r)) return true;
            if (typeof r === "object" && r.hasNewFile) return true;
            return hasNonEmptyString(r);
          }));

      if (!hasAnyData) {
        errors.push("Fill at least one field: instructions, style, budget, date, or a reference image");
        return { valid: false, errors };
      }

      return { valid: true, errors: [] };
    }

    if (data.sourceType === "template") {
      if (!data.templateRef) {
        errors.push("Select a template before continuing");
        return { valid: false, errors };
      }

      const isValidId =
        typeof data.templateRef === "string" &&
        /^[a-f0-9]{24}$/i.test(data.templateRef);

      if (!isValidId) {
        errors.push("Invalid template ID");
        return { valid: false, errors };
      }

      return { valid: true, errors: [] };
    }

    return { valid: false, errors: ["Unknown validation case"] };
  }, [mode]);

  const extractFilesFromPayload = useCallback((payload) => {
    const files = [];

    const cleaned = JSON.parse(
      JSON.stringify(payload, (key, value) => {
        if (isBrowserFile(value)) return undefined;
        return value;
      })
    );

    (payload.slots || []).forEach((slot, slotIndex) => {
      (slot.slotState?.layers || []).forEach((layer, layerIndex) => {
        const fileObj =
          (isBrowserFile(layer.file) && layer.file) ||
          (isBrowserFile(layer._fileBlob) && layer._fileBlob) ||
          null;

        const cleanLayer =
          cleaned.slots?.[slotIndex]?.slotState?.layers?.[layerIndex];

        if (!cleanLayer) return;

        if (fileObj) {
          const fieldName = `layer_${slotIndex}_${layerIndex}`;

          files.push({
            slotIndex,
            layerIndex,
            file: fileObj,
            fieldName
          });

          cleanLayer.hasNewFile = true;
          cleanLayer.fileFieldName = fieldName;
          cleanLayer.urlImg = "";
        } else if (
          cleanLayer.urlImg &&
          (cleanLayer.urlImg.startsWith("blob:") ||
            cleanLayer.urlImg.startsWith("data:"))
        ) {
          cleanLayer.urlImg = "";
        }

        delete cleanLayer.file;
        delete cleanLayer._fileBlob;
      });
    });

    const askArray = Array.isArray(payload.askSellerData)
      ? payload.askSellerData
      : payload.askSellerData
        ? [payload.askSellerData]
        : [];

    askArray.forEach((ask, askIndex) => {
      (ask.images || []).forEach((ref, refIndex) => {
        if (!isBrowserFile(ref)) return;

        const fieldName = `askref_${askIndex}_${refIndex}`;

        files.push({
          slotIndex: -1,
          layerIndex: -1,
          file: ref,
          fieldName
        });

        const cleanAskArray = Array.isArray(cleaned.askSellerData)
          ? cleaned.askSellerData
          : cleaned.askSellerData
            ? [cleaned.askSellerData]
            : [];

        const cleanAsk = cleanAskArray[askIndex];

        if (cleanAsk) {
          if (!Array.isArray(cleanAsk.images)) cleanAsk.images = [];

          cleanAsk.images[refIndex] = {
            hasNewFile: true,
            fileFieldName: fieldName
          };
        }
      });
    });

    return { cleanedPayload: cleaned, files };
  }, []);

  const buildGraphicWrapper = useCallback(
    (cleanedPayload, graphicMadeFlag = null) => {
      const slots = (cleanedPayload.slots || []).map((slot) => ({
        ...slot,
        ...(graphicMadeFlag ? { graphicMade: graphicMadeFlag } : {})
      }));

      return {
        products: [
          {
            productID,
            graphic: {
              sourceType: cleanedPayload.sourceType,
              slots,
              askSellerData: cleanedPayload.askSellerData ?? null,
              templateMeta: cleanedPayload.templateMeta ?? null,
              templateRef: cleanedPayload.templateRef ?? null,
              sellerNotes:
                cleanedPayload.sellerNotes || cleanedPayload.notes || ""
            }
          }
        ]
      };
    },
    [productID]
  );

  const buildSellerGraphicData = useCallback(
    (data) => {
      const { cleanedPayload } = extractFilesFromPayload(data);
      return buildGraphicWrapper(cleanedPayload, "seller");
    },
    [extractFilesFromPayload, buildGraphicWrapper]
  );

  const sendMultipart = useCallback(
    async (data, opts = {}) => {
      const { updateOrderGraphicMultipart } = await import("../../../auth/apiProduct");

      const { cleanedPayload, files } = extractFilesFromPayload(data);

      const wrapper = buildGraphicWrapper(
        cleanedPayload,
        opts.graphicMade || null
      );

      const formData = new FormData();

      formData.append(
        "data",
        JSON.stringify({
          products: wrapper.products,
          sellerNotes: cleanedPayload.sellerNotes || cleanedPayload.notes || ""
        })
      );

      files.forEach(({ file, fieldName }) => {
        formData.append(fieldName, file);
      });

      return await updateOrderGraphicMultipart(
        { token: authToken, pubToken },
        orderId,
        formData,
        {
          mode: opts.mode || mode,
          status: opts.status || "in_review",
          apiUrl
        }
      );
    },
    [
      authToken,
      pubToken,
      orderId,
      mode,
      apiUrl,
      extractFilesFromPayload,
      buildGraphicWrapper
    ]
  );

  const computeHash = useCallback((data) => {
    try {
      const fileSignature = (file) => {
        return isBrowserFile(file) ? `file:${file.name}:${file.size}` : null;
      };

      return JSON.stringify({
        sourceType: data?.sourceType ?? null,
        slots: (data?.slots || []).map((slot) => ({
          id: slot.id || slot.slotId || null,
          label: slot.label,
          url: slot.url || slot.localUrl || "",
          graphicMade: slot.graphicMade,
          texts: (slot.slotState?.texts || []).map((t) => ({
            c: t.content,
            sz: t.size,
            col: t.color,
            rot: t.rotation,
            op: t.opacity,
            x: t.pos?.x,
            y: t.pos?.y,
            font: t.fontId,
            grad: t.gradient
          })),
          layers: (slot.slotState?.layers || []).map((l) => ({
            id: l.originalLayerId || l._id || null,
            file: fileSignature(l.file) || fileSignature(l._fileBlob),
            url: l.urlImg || l.src || l.url || "",
            size: l.size,
            rotation: l.rotation,
            opacity: l.opacity,
            x: l.pos?.x,
            y: l.pos?.y
          })),
          instructions: slot.slotState?.instructions || ""
        })),
        askSellerData: data?.askSellerData ?? null,
        templateRef: data?.templateRef ?? null,
        templateMeta: data?.templateMeta ?? null,
        sellerNotes: data?.sellerNotes ?? "",
        variant: data?.variant ?? null,
        qty: data?.qty ?? null
      });
    } catch {
      return `fallback:${Date.now()}`;
    }
  }, []);

  const writeLocalStorage = useCallback(
    (data) => {
      if (!productID) return false;

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const store = raw ? JSON.parse(raw) : {};
        const now = Date.now();

        Object.keys(store).forEach((key) => {
          if (store[key]?.expiresAt && store[key].expiresAt < now) {
            delete store[key];
          }
        });

        store[productID] = {
          ...data,
          savedAt: now,
          expiresAt: now + STORAGE_TTL
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        return true;
      } catch (err) {
        console.warn("[writeLocalStorage] failed:", err);
        return false;
      }
    },
    [productID]
  );

  const readLocalStorage = useCallback(() => {
    if (!productID) return null;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const store = JSON.parse(raw);
      const entry = store[productID];

      if (!entry) return null;

      if (entry.expiresAt && entry.expiresAt < Date.now()) {
        delete store[productID];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        return null;
      }

      return entry;
    } catch (err) {
      console.warn("[readLocalStorage] failed:", err);
      return null;
    }
  }, [productID]);

  const clearLocalStorage = useCallback(() => {
    if (!productID) return false;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return true;

      const store = JSON.parse(raw);
      delete store[productID];

      if (Object.keys(store).length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
      }

      return true;
    } catch (err) {
      console.warn("[clearLocalStorage] failed:", err);
      return false;
    }
  }, [productID]);

  const saveDraft = useCallback(
    async (data, options = {}) => {
      const { silent = false, force = false } = options;

      if (!data) {
        setError(elakayAutoText("h_f882c9fa90ffef", "No data to save"));
        return false;
      }

      if (!productID) {
        setError(elakayAutoText("h_f1d8b248660312", "Product ID missing"));
        return false;
      }

      const hash = computeHash(data);

      if (!force && hash === lastHashRef.current) {
        return true;
      }

      if (!silent) {
        setBusy(true);
        setBusyMode("draft");
      }

      setError(null);
      setValidationErrors([]);

      const { cleanedPayload } = extractFilesFromPayload(data);

      const success = writeLocalStorage({
        productID,
        payload: cleanedPayload,
        sourceType: data.sourceType,
        variant: data.variant,
        qty: data.qty,
        totalPrice: data.totalPrice
      });

      if (success) {
        lastHashRef.current = hash;
        setSavedStatus("draft");
        setLastSavedAt(Date.now());
        setHasDraft(true);
      } else {
        setError(elakayAutoText("h_4f2a2b435cd894", "Failed to save draft locally"));
      }

      if (!silent) {
        setBusy(false);
        setBusyMode(null);
      }

      return success;
    },
    [productID, computeHash, extractFilesFromPayload, writeLocalStorage]
  );

  const autoSaveDraft = useCallback(
    (data) => {
      if (!enableAutoSave || !data) return;

      lastPayloadRef.current = data;

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      autoSaveTimerRef.current = setTimeout(() => {
        saveDraft(data, { silent: true });
      }, AUTOSAVE_DELAY);
    },
    [enableAutoSave, saveDraft]
  );

  // ── Auto-save : filet de sécurité toutes les 10 min, quelle que soit
  //    l'activité -- le debounce ci-dessus ne se déclenche jamais si le
  //    client modifie son design en continu sans jamais s'arrêter 30s.
  //    saveDraft() est déjà dirty-gated en interne (hash inchangé -> no-op),
  //    donc l'appeler à l'aveugle ici est sûr et bon marché. ─────────────────
  useEffect(() => {
    if (!enableAutoSave) return undefined;
    const id = setInterval(() => {
      if (!lastPayloadRef.current) return;
      saveDraft(lastPayloadRef.current, { silent: true });
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enableAutoSave, saveDraft]);

  const saveDraftServer = useCallback(
    async (data) => {
      if (!orderId || mode !== "seller") {
        return await saveDraft(data, { force: true });
      }

      setBusy(true);
      setBusyMode("draft");
      setError(null);
      setValidationErrors([]);

      try {
        const result = await sendMultipart(data, {
          mode: "seller",
          status: "in_review",
          graphicMade: "seller"
        });

        if (result?.success === false) {
          throw new Error(result.error || "Save failed");
        }

        setSavedStatus("draft");
        setLastSavedAt(Date.now());
        setBusy(false);
        setBusyMode(null);

        return { success: true, serverResponse: result };
      } catch (err) {
        console.error("[saveDraftServer] Error:", err);

        setError(err.message || elakayAutoText("h_4b0265323ed43a", "Failed to save draft"));
        setBusy(false);
        setBusyMode(null);

        return { success: false, error: err.message };
      }
    },
    [orderId, mode, sendMultipart, saveDraft]
  );

  const checkout = useCallback(
    async (data) => {
      if (!data) {
        setError(elakayAutoText("h_72e8052f2192ee", "No data to checkout"));
        return false;
      }

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      setBusy(true);
      setBusyMode("checkout");
      setError(null);
      setValidationErrors([]);

      const validation = validate(data);

      if (!validation.valid) {
        setValidationErrors(validation.errors);
        setError(validation.errors[0]);
        setBusy(false);
        setBusyMode(null);

        onCheckoutError({
          message: __elakayText("t001", "Validation failed"),
          errors: validation.errors
        });

        return false;
      }

      if (mode === "seller" && orderId) {
        try {
          const result = await sendMultipart(data, {
            mode: "seller",
            status: "draft_ready",
            graphicMade: "seller"
          });

          if (result?.success === false) {
            throw new Error(result.error || "Update failed");
          }

          setSavedStatus("submitted");
          setLastSavedAt(Date.now());
          setBusy(false);
          setBusyMode(null);

          onCheckoutSuccess({
            ...data,
            serverResponse: result
          });

          return true;
        } catch (err) {
          console.error("[checkout seller] Error:", err);

          setError(err.message || elakayAutoText("h_23778b5f7eb26d", "Failed to save"));
          setBusy(false);
          setBusyMode(null);

          onCheckoutError({ message: err.message });

          return false;
        }
      }

      if (!productID) {
        setError(elakayAutoText("h_f1d8b248660312", "Product ID missing"));
        setBusy(false);
        setBusyMode(null);
        return false;
      }

      const { cleanedPayload, files } = extractFilesFromPayload(data);

      writeLocalStorage({
        productID,
        payload: cleanedPayload,
        sourceType: data.sourceType,
        variant: data.variant,
        qty: data.qty,
        totalPrice: data.totalPrice
      });

      lastHashRef.current = computeHash(data);

      setSavedStatus("submitted");
      setLastSavedAt(Date.now());
      setBusy(false);
      setBusyMode(null);

      onCheckoutSuccess({
        ...data,
        cleanedPayload,
        files
      });

      return true;
    },
    [
      productID,
      mode,
      orderId,
      validate,
      sendMultipart,
      extractFilesFromPayload,
      writeLocalStorage,
      computeHash,
      onCheckoutSuccess,
      onCheckoutError
    ]
  );

/* ══════════════════════════════════════════════════════
   hasLayerImage
   
   Retourne true s'il existe AU MOINS un layer avec une image :
   - un File (nouveau fichier à uploader)
   - un marqueur hasNewFile
   - une URL HTTP existante
   
   Ne regarde QUE les layers (pas les backgrounds, pas les refs).
══════════════════════════════════════════════════════ */
const hasLayerImage = useCallback((data) => {
  if (!data) return false;

  const isHttpUrl = (u) => typeof u === "string" && u.startsWith("http");

  const slots = data.slots || [];
  for (const slot of slots) {
    const layers = slot.slotState?.layers || [];
    for (const l of layers) {
      if (isBrowserFile(l.file) || isBrowserFile(l._fileBlob)) return true;
      if (l.hasNewFile) return true;
      if (isHttpUrl(l.urlImg) || isHttpUrl(l.src) || isHttpUrl(l.url)) return true;
    }
  }

  return false;
}, []);
  const appendAskRequest = useCallback(
    async (newAskData, currentAskRequests = []) => {
      if (!orderId) {
        setError(elakayAutoText("h_71219e64383442", "Order ID is required to append a request"));
        return { success: false, error: __elakayText("t005", "Order ID missing") };
      }

      if (currentAskRequests.length >= MAX_ASK_REQUESTS) {
        const msg = `Maximum ${MAX_ASK_REQUESTS} ask requests reached`;
        setError(msg);
        return { success: false, error: msg };
      }

      setBusy(true);
      setBusyMode("ask");
      setError(null);
      setValidationErrors([]);

      try {
        const updatedAskData = [
          ...currentAskRequests,
          {
            ...newAskData,
            submittedAt: new Date().toISOString(),
            isRevision: currentAskRequests.length > 0,
            revisionNumber: currentAskRequests.length
          }
        ];

        const askPayload = {
          sourceType: "ask",
          slots: [],
          askSellerData: updatedAskData,
          sellerNotes: ""
        };

        const result = await sendMultipart(askPayload, {
          mode: "customer",
          status: "in_review"
        });

        if (result?.success === false) {
          throw new Error(result.error || "Failed to send request");
        }

        setSavedStatus("submitted");
        setLastSavedAt(Date.now());
        setBusy(false);
        setBusyMode(null);

        /* Use server-saved data (has proper urlImg, no File objects) when available */
        const serverAskData = result?.order?.products?.[0]?.graphic?.askSellerData;
        return {
          success: true,
          askRequests: serverAskData ?? updatedAskData,
          serverResponse: result
        };
      } catch (err) {
        console.error("[appendAskRequest] Error:", err);

        setError(err.message || elakayAutoText("h_261d3591e4ef7a", "Failed to send request"));
        setBusy(false);
        setBusyMode(null);

        return { success: false, error: err.message };
      }
    },
    [orderId, sendMultipart]
  );

  const restore = useCallback(() => {
    const saved = readLocalStorage();

    if (saved?.payload) {
      lastHashRef.current = computeHash(saved.payload);
      setHasDraft(true);
      setSavedStatus("draft");
      setLastSavedAt(saved.savedAt);

      return saved.payload;
    }

    return null;
  }, [readLocalStorage, computeHash]);

  const clear = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    clearLocalStorage();

    lastHashRef.current = null;

    setHasDraft(false);
    setSavedStatus(null);
    setLastSavedAt(null);
    setError(null);
    setValidationErrors([]);
    setBusy(false);
    setBusyMode(null);
  }, [clearLocalStorage]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const saved = readLocalStorage();

    setHasDraft(!!saved);

    if (saved) {
      setSavedStatus("draft");
      setLastSavedAt(saved.savedAt);
    }
  }, [productID, readLocalStorage]);



  return {
    saveDraft,
    saveDraftServer,
    appendAskRequest,
    autoSaveDraft,
    checkout,
    restore,
    clear,
    validate,

    hasLayerImage, 
    extractFilesFromPayload,
    buildGraphicWrapper,
    buildSellerGraphicData,

    clearError: () => {
      setError(null);
      setValidationErrors([]);
    },

    busy,
    busyMode,
    savedStatus,
    lastSavedAt,
    error,
    validationErrors,
    hasDraft
  };
};

export default useGraphicOrderSave;